import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Trash2, Unplug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { useAuthorizedApps, useRevokeApp, type AuthorizedApp } from '@/hooks/use-oauth';
import { toast } from 'sonner';

const SCOPE_LABELS: Record<string, string> = {
  'strategies:read': 'Read strategies',
  'strategies:write': 'Create & edit strategies',
  'backtests:run': 'Run backtests',
  'bots:read': 'Read bots',
  'bots:write': 'Create & manage bots',
  'accounts:read': 'Read accounts',
  'analysis:run': 'Analyze trades',
};

export default function ConnectedAppsPage() {
  const { data: apps, isLoading } = useAuthorizedApps();
  const revokeMutation = useRevokeApp();
  const [revokeTarget, setRevokeTarget] = useState<AuthorizedApp | null>(null);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast.success('App access revoked');
      setRevokeTarget(null);
    } catch {
      toast.error('Failed to revoke app');
    }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <Link
        to="/settings/api-keys"
        className="inline-flex items-center gap-1.5 text-[11px] text-ink-3 hover:text-ink transition-colors"
      >
        <ArrowLeft size={12} /> API Keys
      </Link>

      <div>
        <h1 className="text-xl font-extrabold">Connected Apps</h1>
        <p className="text-xs text-ink-3 mt-0.5">
          Third-party apps you've granted access to your ForgeAlpha account
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : apps && apps.length > 0 ? (
        <div className="space-y-2">
          {apps.map((app) => (
            <div
              key={app.id}
              className="glass-sm rounded-lg p-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {app.client_logo_url ? (
                    <img
                      src={app.client_logo_url}
                      alt=""
                      className="w-8 h-8 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue to-violet flex items-center justify-center text-white text-[10px] font-bold">
                      {app.client_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold">{app.client_name}</p>
                    <p className="text-[10px] text-ink-4">
                      Connected {new Date(app.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {app.scopes.map((s) => (
                    <span
                      key={s}
                      className="text-[9px] font-mono px-1.5 py-0.5 bg-surface rounded text-ink-3"
                    >
                      {SCOPE_LABELS[s] || s}
                    </span>
                  ))}
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={() => setRevokeTarget(app)}>
                <Trash2 size={12} /> Revoke
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-sm rounded-lg p-8 text-center">
          <Unplug size={32} className="mx-auto text-ink-4 mb-3" />
          <p className="text-sm font-semibold text-ink-3">No connected apps</p>
          <p className="text-[11px] text-ink-4 mt-1">
            When you authorize a third-party app, it will appear here.
          </p>
        </div>
      )}

      <Modal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke App Access"
      >
        <div className="space-y-4">
          <p className="text-xs text-ink-2">
            Are you sure you want to revoke <span className="font-bold">{revokeTarget?.client_name}</span>'s access? The app will immediately lose access to your account.
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? <Spinner size="sm" /> : <Trash2 size={12} />}
              Revoke Access
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
